import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { AiComponentInput } from '@/core/ai/app-builder/components/registry'
import {
  AiDialogInput,
  AiMenuInput,
  AiOverlayInput,
  AiPageInput,
  parseAiComponents,
  parseAiDialog,
  parseAiMenu,
  parseAiOverlay,
  parseAiPage,
} from '@/core/ai/app-builder/parser'
import { parsePageTitle } from '@/core/app-builder/parser/variable-text'
import { cn } from '@/lib/cn'
import { useAppRoutingContext } from '@/context/__project__/AppRoutingContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'

type TabType = 'pages' | 'overlays' | 'dialogs' | 'menus'
type ItemType = 'page' | 'overlay' | 'dialog' | 'menu'
type JsonModalType =
  | 'edit-element-settings'
  | 'import-pages'
  | 'import-overlays'
  | 'import-dialogs'
  | 'import-menus'
  | 'import-elements'

const TAB_CONFIG: Record<
  TabType,
  {
    label: string
    color: string
    activeBg: string
    activeBadgeBg: string
    headerColor: string
    importButtonBg: string
    importButtonHover: string
  }
> = {
  pages: {
    label: 'Pages',
    color: 'blue',
    activeBg: 'bg-blue-600',
    activeBadgeBg: 'bg-blue-500',
    headerColor: 'text-blue-400',
    importButtonBg: 'bg-blue-600',
    importButtonHover: 'hover:bg-blue-700',
  },
  overlays: {
    label: 'Overlays',
    color: 'green',
    activeBg: 'bg-green-600',
    activeBadgeBg: 'bg-green-500',
    headerColor: 'text-green-400',
    importButtonBg: 'bg-green-600',
    importButtonHover: 'hover:bg-green-700',
  },
  dialogs: {
    label: 'Dialogs',
    color: 'purple',
    activeBg: 'bg-purple-600',
    activeBadgeBg: 'bg-purple-500',
    headerColor: 'text-purple-400',
    importButtonBg: 'bg-purple-600',
    importButtonHover: 'hover:bg-purple-700',
  },
  menus: {
    label: 'Menus',
    color: 'orange',
    activeBg: 'bg-orange-600',
    activeBadgeBg: 'bg-orange-500',
    headerColor: 'text-orange-400',
    importButtonBg: 'bg-orange-600',
    importButtonHover: 'hover:bg-orange-700',
  },
}

const ITEM_TYPE_CONFIG: Record<
  ItemType,
  {
    selectedBg: string
    selectedBorder: string
    hoverBg: string
    hoverBorder: string
  }
> = {
  page: {
    selectedBg: 'bg-blue-800',
    selectedBorder: 'border-blue-500',
    hoverBg: 'hover:bg-gray-750',
    hoverBorder: 'hover:border-blue-500',
  },
  overlay: {
    selectedBg: 'bg-green-800',
    selectedBorder: 'border-green-500',
    hoverBg: 'hover:bg-gray-750',
    hoverBorder: 'hover:border-green-500',
  },
  dialog: {
    selectedBg: 'bg-purple-800',
    selectedBorder: 'border-purple-500',
    hoverBg: 'hover:bg-gray-750',
    hoverBorder: 'hover:border-purple-500',
  },
  menu: {
    selectedBg: 'bg-orange-800',
    selectedBorder: 'border-orange-500',
    hoverBg: 'hover:bg-gray-750',
    hoverBorder: 'hover:border-orange-500',
  },
}

const MODAL_TYPE_CONFIG: Record<
  JsonModalType,
  {
    header: string
    borderColor: string
    iconColor: string
    textareaFocusBorder: string
    entityName: string
  }
> = {
  'edit-element-settings': {
    header: 'Edit Settings',
    borderColor: 'border-purple-600',
    iconColor: 'text-purple-400',
    textareaFocusBorder: 'focus:border-purple-500',
    entityName: 'element settings',
  },
  'import-pages': {
    header: 'Import Pages JSON',
    borderColor: 'border-blue-600',
    iconColor: 'text-blue-400',
    textareaFocusBorder: 'focus:border-blue-500',
    entityName: 'pages',
  },
  'import-overlays': {
    header: 'Import Overlays JSON',
    borderColor: 'border-green-600',
    iconColor: 'text-green-400',
    textareaFocusBorder: 'focus:border-green-500',
    entityName: 'overlays',
  },
  'import-dialogs': {
    header: 'Import Dialogs JSON',
    borderColor: 'border-purple-600',
    iconColor: 'text-purple-400',
    textareaFocusBorder: 'focus:border-purple-500',
    entityName: 'dialogs',
  },
  'import-menus': {
    header: 'Import Menus JSON',
    borderColor: 'border-orange-600',
    iconColor: 'text-orange-400',
    textareaFocusBorder: 'focus:border-orange-500',
    entityName: 'menus',
  },
  'import-elements': {
    header: 'Import Elements JSON',
    borderColor: 'border-purple-600',
    iconColor: 'text-purple-400',
    textareaFocusBorder: 'focus:border-purple-500',
    entityName: 'element settings',
  },
}

const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  page: 'Page',
  overlay: 'Overlay',
  dialog: 'Dialog',
  menu: 'Menu',
}

export function AppBuilderDevTools() {
  const { appClient } = useAppBuilderContext()
  const { tablesMap } = useDbClientContext()
  const { pages, overlays, dialogs, menus } = useAppBuilderContext()
  const { activePage } = useAppPagesContext()
  const { goToPage } = useAppRoutingContext()

  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('pages')
  const [selectedItem, setSelectedItem] = useState<{ type: ItemType; id: string } | null>(null)
  const [copiedItemId, setCopiedItemId] = useState<string | null>(null)
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // Unified JSON Modal state
  const [jsonModalType, setJsonModalType] = useState<JsonModalType | null>(null)
  const [jsonModalContent, setJsonModalContent] = useState('')
  const [jsonModalError, setJsonModalError] = useState<string | null>(null)
  const [isJsonModalLoading, setIsJsonModalLoading] = useState(false)

  const selectedItemData = useMemo(() => {
    if (!selectedItem) return null
    switch (selectedItem.type) {
      case 'page':
        return pages.find((page) => page.id === selectedItem.id)
      case 'overlay':
        return overlays.find((overlay) => overlay.id === selectedItem.id)
      case 'dialog':
        return dialogs.find((dialog) => dialog.id === selectedItem.id)
      case 'menu':
        return menus.find((menu) => menu.id === selectedItem.id)
      default:
        return null
    }
  }, [dialogs, menus, overlays, pages, selectedItem])

  const selectedLayerComponents = useMemo(() => {
    if (!selectedItem || (selectedItem.type !== 'page' && selectedItem.type !== 'overlay')) return []
    const componentsMap =
      selectedItem.type === 'page'
        ? appClient.pagesMap.get(selectedItem.id)?.componentsMap
        : appClient.overlaysMap.get(selectedItem.id)?.componentsMap
    return componentsMap ? Array.from(componentsMap.values()) : []
  }, [appClient, selectedItem])

  const filteredPages = useMemo(() => {
    if (!searchQuery) return pages
    const query = searchQuery.toLowerCase()
    return pages.filter((page) => {
      const container = appClient.pagesMap.get(page.id)
      if (!container) return false
      const pageTitle = parsePageTitle(container).toLowerCase()
      return pageTitle.includes(query) || page.slug?.toLowerCase().includes(query)
    })
  }, [pages, searchQuery, appClient])

  // Filter out active page from the regular list and check if active page matches search
  const pagesWithoutActive = useMemo(() => {
    if (!activePage) return filteredPages
    return filteredPages.filter((page) => page.id !== activePage.id)
  }, [filteredPages, activePage])

  const activePageMatchesSearch = useMemo(() => {
    if (!activePage || !searchQuery) return true
    const query = searchQuery.toLowerCase()
    const container = appClient.pagesMap.get(activePage.id)
    if (!container) return false
    const pageTitle = parsePageTitle(container).toLowerCase()
    return pageTitle.includes(query) || activePage.slug?.toLowerCase().includes(query)
  }, [activePage, searchQuery, appClient])

  const filteredOverlays = useMemo(() => {
    if (!searchQuery) return overlays
    const query = searchQuery.toLowerCase()
    return overlays.filter((overlay) => {
      const title = overlay.title?.toLowerCase() || ''
      const slug = overlay.slug?.toLowerCase() || ''
      return title.includes(query) || slug.includes(query)
    })
  }, [overlays, searchQuery])

  // Helper to parse VariableTextValue (can be JSON string or plain string)
  const parseVariableText = (text: string | undefined): string => {
    if (!text) return ''
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed) && parsed[0]?.children?.[0]?.text) {
        return parsed[0].children[0].text
      }
    } catch {
      // Not JSON, use as-is
    }
    return text
  }

  const filteredDialogs = useMemo(() => {
    if (!searchQuery) return dialogs
    const query = searchQuery.toLowerCase()
    return dialogs.filter((dialog) => {
      const name = parseVariableText(dialog.name).toLowerCase()
      const headline = parseVariableText(dialog.headline).toLowerCase()
      return name.includes(query) || headline.includes(query)
    })
  }, [dialogs, searchQuery])

  const filteredMenus = useMemo(() => {
    if (!searchQuery) return menus
    const query = searchQuery.toLowerCase()
    return menus.filter((menu) => {
      const title = parseVariableText(menu.title).toLowerCase()
      return title.includes(query)
    })
  }, [menus, searchQuery])

  // Helper function to get page metadata
  const getPageMetadata = (page: any) => {
    const container = appClient.pagesMap.get(page.id)
    if (!container) return null
    const pageTitle = parsePageTitle(container)
    const tableName = page.tableId ? tablesMap.get(page.tableId)?.settings._Name : ''
    const isFormDetailAdd = page.tableId && page.title.startsWith('form-add')
    const isFormDetailUpdate = page.tableId && page.title.startsWith('form-detail')
    const isFormDetail = page.tableId && !isFormDetailAdd && !isFormDetailUpdate
    return { pageTitle, tableName, isFormDetailAdd, isFormDetailUpdate, isFormDetail }
  }

  // Helper function to render page badges
  const renderPageBadges = (page: any) => {
    const metadata = getPageMetadata(page)
    if (!metadata) return null

    return (
      <div className='shrink-0 flex items-center gap-1'>
        {metadata.isFormDetailAdd ? (
          <span className='text-m-sm px-1.5 bg-green-700 rounded'>Form Add</span>
        ) : metadata.isFormDetailUpdate ? (
          <span className='text-m-sm px-1.5 bg-blue-700 rounded'>Form Update</span>
        ) : metadata.isFormDetail ? (
          <span className='text-m-sm px-1.5 bg-yellow-700 rounded'>Details Page</span>
        ) : (
          <span className='text-m-sm px-1.5 bg-gray-700 rounded'>Standard</span>
        )}
        {!page.enabled && <span className='text-m-sm px-1.5 bg-orange-700 rounded'>Disabled</span>}
      </div>
    )
  }

  // Helper function to render page item
  const renderPageItem = (page: any, isActive = false) => {
    const metadata = getPageMetadata(page)
    if (!metadata) return null

    const isSelected = selectedItem?.type === 'page' && selectedItem.id === page.id
    const baseClasses = 'rounded p-2.5 border transition-colors cursor-pointer'

    const activeClasses = isSelected
      ? 'bg-brand-900 border-brand-500'
      : 'bg-brand-950 border-brand-700 hover:bg-brand-900 hover:border-brand-500'

    const regularClasses = isSelected
      ? 'bg-blue-800 border-blue-500'
      : 'bg-gray-800 hover:bg-gray-750 border-gray-700 hover:border-blue-500'

    return (
      <div
        onClick={() => handleSelectItem('page', page)}
        className={cn(baseClasses, isActive ? activeClasses : regularClasses)}
        title={`Click to view ${metadata.pageTitle} settings`}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center justify-between gap-2'>
              <div className='text-m-s font-medium truncate'>{metadata.pageTitle || 'Untitled'}</div>
              {renderPageBadges(page)}
            </div>
            {metadata.tableName && (
              <div className='flex items-center gap-2 mt-1 text-m-s text-gray-200 overflow-hidden'>
                <span className='text-m-sm px-1.5 bg-gray-700 rounded truncate max-w-[120px]'>
                  {metadata.tableName}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const handleCopySettings = (item: any) => {
    const jsonString = JSON.stringify(item, null, 2)
    navigator.clipboard.writeText(jsonString)
    setCopiedItemId(item.id)
    setTimeout(() => setCopiedItemId(null), 2000)
  }

  const handleSelectItem = (type: ItemType, data: any) => {
    setSelectedItem({ type, id: data.id })
    setExpandedComponents(new Set()) // Reset expanded components when selecting new item
    setJsonModalType(null) // Close modal when selecting new item

    // Update active tab to match selected item type
    const tabMap: Record<ItemType, TabType> = {
      page: 'pages',
      overlay: 'overlays',
      dialog: 'dialogs',
      menu: 'menus',
    }
    setActiveTab(tabMap[type])
  }

  // Unified JSON Modal handlers
  const handleOpenJsonModal = (type: JsonModalType) => {
    if (type === 'edit-element-settings') {
      if (!selectedLayerComponents.length) return
      const componentsSettings = Object.fromEntries(
        selectedLayerComponents.map((component) => [component._id, component.settings])
      )
      setJsonModalContent(JSON.stringify(componentsSettings, null, 2))
    } else {
      setJsonModalContent('')
    }
    setJsonModalError(null)
    setJsonModalType(type)
  }

  const handleCloseJsonModal = () => {
    setJsonModalType(null)
    setJsonModalContent('')
    setJsonModalError(null)
  }

  const handleValidateJsonModal = (value: string) => {
    setJsonModalContent(value)
    try {
      JSON.parse(value)
      setJsonModalError(null)
    } catch (error) {
      setJsonModalError((error as Error).message)
    }
  }

  const handleImportJsonFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        // Validate JSON first
        JSON.parse(content)
        setJsonModalContent(content)
        setJsonModalError(null)
      } catch (error) {
        setJsonModalError((error as Error).message)
        toast.error('Invalid JSON file')
      }
    }
    reader.onerror = () => {
      toast.error('Failed to read file')
    }
    reader.readAsText(file)

    // Reset the input so the same file can be selected again
    event.target.value = ''
  }

  // Save JSON to app builder structures
  const handleSave = async () => {
    if (!jsonModalType) return

    setIsJsonModalLoading(true)

    try {
      let parsedContent = JSON.parse(jsonModalContent)

      // make sure content is array
      if (!Array.isArray(parsedContent)) {
        parsedContent = [parsedContent]
      }

      let shouldClose = false

      switch (jsonModalType) {
        case 'import-pages':
          {
            const pagesToImport = parsedContent as AiPageInput[]

            let firstPageSlug = ''

            await Promise.all(
              pagesToImport.map(async (page) => {
                const payload = parseAiPage(page)

                // make sure page title is unique, add +n prefix accordingly
                let existingPageTitleLength = pages.filter((p) => p.title === payload._Settings._Title).length
                if (existingPageTitleLength > 0) {
                  payload._Settings._Title = `${payload._Settings._Title}-${existingPageTitleLength}`
                }

                // make sure page slug is unique, add +n prefix accordingly
                let existingPageSlugLength = pages.filter((p) => p.slug === payload._Slug).length
                if (existingPageSlugLength > 0) {
                  payload._Slug = `${payload._Slug}-${existingPageSlugLength}`
                }

                const newPage = await appClient.AddPage(payload)
                if (!firstPageSlug) {
                  firstPageSlug = newPage._Slug
                }
              })
            )
            if (firstPageSlug) {
              goToPage({ slug: firstPageSlug })
            }
            toast.success('Pages imported successfully')
            shouldClose = true
          }
          break
        case 'import-overlays':
          {
            const overlays = parsedContent as AiOverlayInput[]

            await Promise.all(
              overlays.map(async (overlay) => {
                const payload = parseAiOverlay(overlay)
                await appClient.AddOverlay(payload)
              })
            )

            toast.success('Overlays imported successfully')
            shouldClose = true
          }
          break
        case 'import-dialogs':
          {
            const dialogs = parsedContent as AiDialogInput[]

            await Promise.all(
              dialogs.map(async (dialog) => {
                const payload = parseAiDialog(dialog)
                await appClient.AddDialog(payload)
              })
            )

            toast.success('Dialogs imported successfully')
            shouldClose = true
          }
          break
        case 'import-menus':
          {
            const menus = parsedContent as AiMenuInput[]

            await Promise.all(
              menus.map(async (menu) => {
                const payload = parseAiMenu(menu)
                await appClient.AddMenu(payload)
              })
            )

            toast.success('Menus imported successfully')
            shouldClose = true
          }
          break
        case 'import-elements':
          {
            if (!selectedItem) return

            const components = parsedContent as AiComponentInput[]
            const { bodies, bodySettings } = parseAiComponents(components)

            const layer =
              selectedItem?.type === 'page'
                ? appClient.pagesMap.get(selectedItem.id)
                : appClient.overlaysMap.get(selectedItem.id)

            if (!layer) return

            const newBody = [...layer.settings._Body, ...bodies]
            const newBodySettings = {
              ...layer.settings._BodySettings,
              ...bodySettings,
            }

            await layer.Update(
              {
                _Body: newBody,
                _BodySettings: newBodySettings,
              },
              true,
              { description: `Import components to ${selectedItem?.type}` }
            )

            toast.success('Components imported successfully')
          }
          break
        case 'edit-element-settings':
          {
            if (!selectedItem) return

            const layer =
              selectedItem?.type === 'page'
                ? appClient.pagesMap.get(selectedItem.id)
                : appClient.overlaysMap.get(selectedItem.id)

            if (!layer) return

            await layer.Update(
              {
                _BodySettings: parsedContent,
              },
              true,
              { description: `Update ${selectedItem?.type === 'page' ? 'page' : 'overlay'} settings` }
            )

            toast.success('Settings saved successfully')
          }
          break
        default:
          break
      }

      setJsonModalError(null)
      handleCloseJsonModal()
      if (shouldClose) setIsOpen(false)
    } catch (error) {
      setJsonModalError((error as Error).message)
    } finally {
      setIsJsonModalLoading(false)
    }
  }

  const toggleComponentExpanded = (componentId: string) => {
    setExpandedComponents((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(componentId)) {
        newSet.delete(componentId)
      } else {
        newSet.add(componentId)
      }
      return newSet
    })
  }

  const handleCopyComponentSettings = (componentId: string, settings: any) => {
    const jsonString = JSON.stringify(settings, null, 2)
    navigator.clipboard.writeText(jsonString)
    setCopiedItemId(componentId)
    setTimeout(() => setCopiedItemId(null), 2000)
  }

  const getModalConfig = (type: JsonModalType | null) => {
    if (!type) return null
    return MODAL_TYPE_CONFIG[type]
  }

  const getSelectedItemTitle = (item: any, type: ItemType | null): string => {
    if (!type || !item) return 'Untitled'
    if (type === 'dialog') {
      return parseVariableText(item.name) || parseVariableText(item.headline) || 'Untitled'
    }
    if (type === 'menu') {
      return parseVariableText(item.title) || 'Untitled'
    }
    return item.title || 'Untitled'
  }

  return (
    <>
      {/* Toggle Button - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='fixed bottom-4 right-38 z-[9999] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg font-semibold text-m-m flex items-center gap-2'
        title={isOpen ? 'Minimize App Builder Devtools' : 'Open App Builder Devtools'}
      >
        <MaterialIcon icon='settings' size={14} />
        Builder Devtools
      </button>

      {/* Popup Panel */}
      {isOpen && (
        <div className='fixed bottom-16 right-40 z-[9999] w-[900px] h-[700px] bg-gray-900 text-white rounded-lg border-2 border-blue-600 shadow-2xl flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700 rounded-t-lg'>
            <div className='flex items-center gap-3'>
              <MaterialIcon icon='description' size={14} className='text-blue-400' />
              <h3 className='font-semibold text-m-m'>App Builder Devtools</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className='cursor-pointer text-gray-400 hover:text-white transition-colors'
              title='Close Devtools'
            >
              <MaterialIcon icon='close' size={20} />
            </button>
          </div>

          {/* Horizontal Tabs */}
          <div className='flex items-center justify-between gap-1 px-2 py-2 bg-gray-800 border-b border-gray-700'>
            <div className='flex items-center gap-1'>
              {(Object.keys(TAB_CONFIG) as TabType[]).map((tabKey) => {
                const config = TAB_CONFIG[tabKey]
                const isActive = activeTab === tabKey
                const getCount = () => {
                  if (tabKey === 'pages') {
                    return searchQuery
                      ? `${pagesWithoutActive.length + (activePage && activePageMatchesSearch ? 1 : 0)}/${pages.length}`
                      : pages.length
                  }
                  if (tabKey === 'overlays') {
                    return searchQuery ? `${filteredOverlays.length}/${overlays.length}` : overlays.length
                  }
                  if (tabKey === 'dialogs') {
                    return searchQuery ? `${filteredDialogs.length}/${dialogs.length}` : dialogs.length
                  }
                  if (tabKey === 'menus') {
                    return searchQuery ? `${filteredMenus.length}/${menus.length}` : menus.length
                  }
                  return 0
                }

                return (
                  <button
                    key={tabKey}
                    onClick={() => {
                      setActiveTab(tabKey)
                      setSelectedItem(null)
                    }}
                    className={cn(
                      'cursor-pointer px-4 py-1.5 rounded-t text-m-s font-medium transition-colors flex items-center gap-2',
                      isActive ? `${config.activeBg} text-white` : 'bg-gray-700 text-gray-300 hover:bg-gray-650'
                    )}
                  >
                    <span>{config.label}</span>
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-m-sm font-semibold',
                        isActive ? `${config.activeBadgeBg} text-white` : 'bg-gray-600 text-gray-200'
                      )}
                    >
                      {getCount()}
                    </span>
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => {
                const importTypeMap: Record<TabType, JsonModalType> = {
                  pages: 'import-pages',
                  overlays: 'import-overlays',
                  dialogs: 'import-dialogs',
                  menus: 'import-menus',
                }
                handleOpenJsonModal(importTypeMap[activeTab])
              }}
              className={cn(
                'text-m-sm px-3 py-1.5 text-white rounded transition-colors flex items-center gap-1.5 font-medium',
                TAB_CONFIG[activeTab].importButtonBg,
                TAB_CONFIG[activeTab].importButtonHover
              )}
              title='AI JSON Import'
            >
              <MaterialIcon icon='upload' size={14} />
              AI JSON Import
            </button>
          </div>

          {/* Content */}
          <div className='flex flex-1 divide-x divide-gray-700 overflow-hidden'>
            {/* Left Panel - Tabbed Content */}
            <div className='w-[300px] flex flex-col overflow-hidden'>
              {/* Search Input */}
              <div className='bg-gray-800 px-3 py-2 border-b border-gray-700'>
                <div className='relative'>
                  <MaterialIcon
                    icon='search'
                    size={16}
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
                  />
                  <input
                    type='text'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search ${activeTab}...`}
                    className='w-full bg-gray-900 text-gray-300 text-m-s pl-9 pr-8 py-1.5 rounded border border-gray-700 focus:border-blue-500 focus:outline-none transition-colors'
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className='absolute right-2 top-1/2 -translate-y-[40%] text-gray-400 hover:text-white transition-colors'
                      title='Clear search'
                    >
                      <MaterialIcon icon='close' size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Tab Content */}
              <div className='flex-1 flex flex-col overflow-hidden'>
                {activeTab === 'pages' && (
                  <>
                    {/* Active Page Section */}
                    {activePage && activePageMatchesSearch && (
                      <>
                        <div className='bg-gray-800 px-4 py-2 text-xs font-semibold text-brand-400 border-b border-gray-700 flex items-center justify-between'>
                          <span className='flex items-center gap-2'>Active Page</span>
                        </div>
                        <div className='p-2 border-b border-gray-700'>{renderPageItem(activePage, true)}</div>
                      </>
                    )}

                    {/* Pages List */}
                    <div
                      className={cn(
                        'bg-gray-800 px-4 py-2 text-xs font-semibold border-b border-gray-700',
                        TAB_CONFIG.pages.headerColor
                      )}
                    >
                      <span>
                        Pages ({pagesWithoutActive.length}
                        {searchQuery && ` / ${pages.length}`})
                      </span>
                    </div>
                    <div className='flex-1 overflow-y-auto p-2 space-y-1.5'>
                      {pagesWithoutActive.length === 0 ? (
                        <div className='text-center text-gray-500 text-m-s py-4'>
                          {searchQuery ? 'No matching pages' : 'No pages'}
                        </div>
                      ) : (
                        pagesWithoutActive.map((page) => <div key={page.id}>{renderPageItem(page, false)}</div>)
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'overlays' && (
                  <>
                    <div
                      className={cn(
                        'bg-gray-800 px-4 py-2 text-xs font-semibold border-b border-gray-700',
                        TAB_CONFIG.overlays.headerColor
                      )}
                    >
                      <span>
                        Overlays ({filteredOverlays.length}
                        {searchQuery && ` / ${overlays.length}`})
                      </span>
                    </div>
                    <div className='flex-1 overflow-y-auto p-2 space-y-1.5'>
                      {filteredOverlays.length === 0 ? (
                        <div className='text-center text-gray-500 text-m-s py-4'>
                          {searchQuery ? 'No matching overlays' : 'No overlays'}
                        </div>
                      ) : (
                        filteredOverlays.map((overlay) => {
                          const itemConfig = ITEM_TYPE_CONFIG.overlay
                          const isSelected = selectedItem?.type === 'overlay' && selectedItem.id === overlay.id
                          return (
                            <div
                              key={overlay.id}
                              onClick={() => handleSelectItem('overlay', overlay)}
                              className={cn(
                                'rounded p-2.5 border transition-colors cursor-pointer',
                                isSelected
                                  ? `${itemConfig.selectedBg} ${itemConfig.selectedBorder}`
                                  : `bg-gray-800 ${itemConfig.hoverBg} border-gray-700 ${itemConfig.hoverBorder}`
                              )}
                              title={`Click to view ${overlay.title} settings`}
                            >
                              <div className='flex items-start justify-between gap-2'>
                                <div className='flex-1 min-w-0'>
                                  <div className='text-m-s font-medium truncate'>{overlay.title || 'Untitled'}</div>
                                  <div className='flex items-center gap-2 mt-1 text-m-s text-gray-400'>
                                    {!overlay.enabled && (
                                      <span className='text-m-sm px-1.5 bg-orange-700 rounded'>Disabled</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'dialogs' && (
                  <>
                    <div
                      className={cn(
                        'bg-gray-800 px-4 py-2 text-xs font-semibold border-b border-gray-700',
                        TAB_CONFIG.dialogs.headerColor
                      )}
                    >
                      <span>
                        Dialogs ({filteredDialogs.length}
                        {searchQuery && ` / ${dialogs.length}`})
                      </span>
                    </div>
                    <div className='flex-1 overflow-y-auto p-2 space-y-1.5'>
                      {filteredDialogs.length === 0 ? (
                        <div className='text-center text-gray-500 text-m-s py-4'>
                          {searchQuery ? 'No matching dialogs' : 'No dialogs'}
                        </div>
                      ) : (
                        filteredDialogs.map((dialog) => {
                          const itemConfig = ITEM_TYPE_CONFIG.dialog
                          const isSelected = selectedItem?.type === 'dialog' && selectedItem.id === dialog.id
                          const dialogName = parseVariableText(dialog.name) || 'Untitled'
                          const dialogHeadline = parseVariableText(dialog.headline) || ''
                          return (
                            <div
                              key={dialog.id}
                              onClick={() => handleSelectItem('dialog', dialog)}
                              className={cn(
                                'rounded p-2.5 border transition-colors cursor-pointer',
                                isSelected
                                  ? `${itemConfig.selectedBg} ${itemConfig.selectedBorder}`
                                  : `bg-gray-800 ${itemConfig.hoverBg} border-gray-700 ${itemConfig.hoverBorder}`
                              )}
                              title={`Click to view ${dialogName} settings`}
                            >
                              <div className='flex items-start justify-between gap-2'>
                                <div className='flex-1 min-w-0'>
                                  <div className='text-m-s font-medium truncate'>{dialogName}</div>
                                  {dialogHeadline && (
                                    <div className='text-m-sm text-gray-400 mt-1 truncate'>{dialogHeadline}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'menus' && (
                  <>
                    <div
                      className={cn(
                        'bg-gray-800 px-4 py-2 text-xs font-semibold border-b border-gray-700',
                        TAB_CONFIG.menus.headerColor
                      )}
                    >
                      <span>
                        Menus ({filteredMenus.length}
                        {searchQuery && ` / ${menus.length}`})
                      </span>
                    </div>
                    <div className='flex-1 overflow-y-auto p-2 space-y-1.5'>
                      {filteredMenus.length === 0 ? (
                        <div className='text-center text-gray-500 text-m-s py-4'>
                          {searchQuery ? 'No matching menus' : 'No menus'}
                        </div>
                      ) : (
                        filteredMenus.map((menu) => {
                          const itemConfig = ITEM_TYPE_CONFIG.menu
                          const isSelected = selectedItem?.type === 'menu' && selectedItem.id === menu.id
                          const menuTitle = parseVariableText(menu.title) || 'Untitled'
                          return (
                            <div
                              key={menu.id}
                              onClick={() => handleSelectItem('menu', menu)}
                              className={cn(
                                'rounded p-2.5 border transition-colors cursor-pointer',
                                isSelected
                                  ? `${itemConfig.selectedBg} ${itemConfig.selectedBorder}`
                                  : `bg-gray-800 ${itemConfig.hoverBg} border-gray-700 ${itemConfig.hoverBorder}`
                              )}
                              title={`Click to view ${menuTitle} settings`}
                            >
                              <div className='flex items-start justify-between gap-2'>
                                <div className='flex-1 min-w-0'>
                                  <div className='text-m-s font-medium truncate'>{menuTitle}</div>
                                  <div className='text-m-sm text-gray-400 mt-1'>
                                    {menu.items?.length || 0} item{menu.items?.length !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Panel - Split into Element Settings (top) and Full JSON (bottom) */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              {/* Top Section - Element Settings Accordion */}
              <div className='flex-1 flex flex-col overflow-hidden border-b border-gray-700'>
                <div className='bg-gray-800 px-4 py-2 text-xs font-semibold text-purple-400 border-b border-gray-700'>
                  <span>Element Settings ({selectedLayerComponents.length})</span>
                </div>

                <div className='flex-1 overflow-y-auto p-2 bg-gray-900'>
                  {selectedLayerComponents.length > 0 ? (
                    selectedLayerComponents.length === 0 ? (
                      <div className='flex flex-col items-center justify-center h-full text-gray-500'>
                        <p className='text-m-s'>No element settings</p>
                      </div>
                    ) : (
                      <div className='space-y-1'>
                        {selectedLayerComponents.map((component) => {
                          const label = component._Title
                            ? `${component._Title} (${component._ComponentID})`
                            : component._ComponentID

                          const isExpanded = expandedComponents.has(component._id)
                          return (
                            <div key={component._id} className='border border-gray-700 rounded overflow-hidden'>
                              {/* Accordion Header */}
                              <div
                                onClick={() => toggleComponentExpanded(component._id)}
                                className='cursor-pointer w-full bg-gray-800 hover:bg-gray-750 px-3 py-2 flex items-center justify-between transition-colors'
                              >
                                <div className='flex items-center gap-2 flex-1 min-w-0'>
                                  <MaterialIcon
                                    icon='chevron_right'
                                    size={16}
                                    className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                  />
                                  <div>
                                    <div className='text-m-s text-left truncate'>{label}</div>
                                    <div className='text-[11px] text-gray-500 font-mono text-left truncate'>
                                      {component._id}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopyComponentSettings(component._id, component.settings)
                                  }}
                                  className='ml-2 p-1 hover:bg-gray-700 rounded transition-colors'
                                  title='Copy component settings'
                                >
                                  {copiedItemId === component._id ? (
                                    <MaterialIcon icon='check' size={16} className='text-green-400' />
                                  ) : (
                                    <MaterialIcon icon='content_copy' size={16} className='text-gray-400' />
                                  )}
                                </button>
                              </div>

                              {/* Accordion Content */}
                              {isExpanded && (
                                <div className='bg-gray-950 p-3 border-t border-gray-700'>
                                  <pre className='text-m-sm text-gray-300 font-mono whitespace-pre-wrap break-words'>
                                    {JSON.stringify(component.settings, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  ) : (
                    <div className='flex flex-col items-center justify-center h-full text-gray-500'>
                      <MaterialIcon icon='tune' size={48} className='mb-3' />
                      <p className='text-m-s'>
                        {selectedItem?.type === 'dialog' || selectedItem?.type === 'menu'
                          ? 'Element settings are only available for pages and overlays'
                          : 'Select a page or overlay to view element settings'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Section - Full Settings JSON */}
              <div className='flex-[0.8] flex flex-col overflow-hidden'>
                <div className='bg-gray-800 px-4 py-2 text-xs font-semibold text-cyan-400 border-b border-gray-700 flex items-center justify-between'>
                  <span>Full Settings JSON</span>
                  {selectedItem && (
                    <div className='flex items-center gap-2'>
                      {(selectedItem?.type === 'page' || selectedItem?.type === 'overlay') && (
                        <>
                          <button
                            onClick={() => handleOpenJsonModal('import-elements')}
                            className='text-m-s px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors flex items-center gap-1'
                            title='Add Elements'
                          >
                            <MaterialIcon icon='add' size={16} />
                            Add Elements
                          </button>
                          <button
                            onClick={() => handleOpenJsonModal('edit-element-settings')}
                            className={`text-m-s px-2 py-1 rounded transition-colors flex items-center gap-1 ${
                              (selectedItemData as any)?.elementSettings
                                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            }`}
                            title={
                              (selectedItemData as any)?.elementSettings
                                ? 'Edit settings'
                                : 'No element settings available'
                            }
                            disabled={!(selectedItemData as any)?.elementSettings}
                          >
                            <MaterialIcon icon='edit' size={16} />
                            Edit Settings
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleCopySettings(selectedItemData)}
                        className='text-m-s px-2 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-white transition-colors flex items-center gap-1'
                        title='Copy JSON to clipboard'
                      >
                        {copiedItemId === selectedItemData?.id ? (
                          <>
                            <MaterialIcon icon='check' size={16} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <MaterialIcon icon='content_copy' size={16} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <div className='flex-1 overflow-y-auto p-4 bg-gray-950'>
                  {selectedItem ? (
                    <pre className='text-m-s text-gray-300 font-mono whitespace-pre-wrap break-words'>
                      {JSON.stringify(selectedItemData, null, 2)}
                    </pre>
                  ) : (
                    <div className='flex flex-col items-center justify-center h-full text-gray-500'>
                      <MaterialIcon icon='description' size={48} className='mb-3' />
                      <p className='text-m-s'>Select a page, overlay, dialog, or menu to view settings</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified JSON Modal */}
      {jsonModalType &&
        (() => {
          const modalConfig = getModalConfig(jsonModalType)
          if (!modalConfig) return null

          return (
            <div className='fixed inset-0 z-[10000] flex items-center justify-center'>
              {/* overlay */}
              <div className='absolute inset-0 bg-black opacity-60' onClick={handleCloseJsonModal} />
              <div
                className={cn(
                  'relative z-[10001] bg-gray-900 rounded-lg border-2 shadow-2xl flex flex-col w-[800px] h-[600px]',
                  modalConfig.borderColor
                )}
              >
                {/* Modal Header */}
                <div className='flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 rounded-t-lg'>
                  <div className='flex items-center gap-3'>
                    <MaterialIcon
                      icon={jsonModalType === 'edit-element-settings' ? 'edit' : 'upload'}
                      size={20}
                      className={modalConfig.iconColor}
                    />
                    <h3 className='font-semibold text-m-l text-white'>{modalConfig.header}</h3>
                    {jsonModalType === 'edit-element-settings' && selectedItem && (
                      <span className='text-m-s text-gray-400'>
                        {ITEM_TYPE_LABELS[selectedItem.type]}:{' '}
                        {getSelectedItemTitle(selectedItemData, selectedItem.type)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleCloseJsonModal}
                    className='cursor-pointer text-gray-400 hover:text-white transition-colors'
                    title='Close Modal'
                  >
                    <MaterialIcon icon='close' size={24} />
                  </button>
                </div>

                {/* Modal Content */}
                <div className='flex-1 flex flex-col overflow-hidden p-4'>
                  <div className='flex-1 flex flex-col overflow-hidden'>
                    <div className='text-m-s text-gray-300 mb-2 flex items-center justify-between'>
                      <span>
                        {jsonModalType === 'edit-element-settings'
                          ? 'Element Settings JSON'
                          : `Paste or edit your ${modalConfig.entityName} JSON below`}
                      </span>
                      <div className='flex items-center gap-2'>
                        {jsonModalError && (
                          <span className='text-red-400 text-m-sm flex items-center gap-1'>
                            <MaterialIcon icon='error' size={16} />
                            Invalid JSON
                          </span>
                        )}
                        <label
                          htmlFor='json-modal-file-input'
                          className='cursor-pointer px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-m-sm transition-colors flex items-center gap-1'
                          title='Import JSON file'
                        >
                          <MaterialIcon icon='upload' size={14} />
                          Import JSON
                        </label>
                        <input
                          id='json-modal-file-input'
                          type='file'
                          accept='.json,application/json'
                          onChange={handleImportJsonFile}
                          className='hidden'
                        />
                      </div>
                    </div>
                    <textarea
                      value={jsonModalContent}
                      onChange={(e) => handleValidateJsonModal(e.target.value)}
                      className={cn(
                        'flex-1 w-full bg-gray-950 text-gray-300 font-mono text-m-s p-4 rounded border-2 transition-colors resize-none focus:outline-none',
                        jsonModalError
                          ? 'border-red-500 focus:border-red-400'
                          : `border-gray-700 ${modalConfig.textareaFocusBorder}`
                      )}
                      spellCheck={false}
                      placeholder={
                        jsonModalType === 'edit-element-settings' ? 'Edit JSON here...' : 'Paste JSON here...'
                      }
                    />
                    {jsonModalError && (
                      <div className='mt-2 p-2 bg-red-900 bg-opacity-30 border border-red-700 rounded text-red-400 text-m-sm'>
                        <strong>Error:</strong> {jsonModalError}
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className='flex items-center justify-between px-4 py-3 bg-gray-800 border-t border-gray-700 rounded-b-lg'>
                  <div className='text-m-s text-gray-400'>
                    <span className='inline-flex items-center gap-1'>
                      <MaterialIcon icon='info' size={16} />
                      {jsonModalType === 'edit-element-settings'
                        ? 'Edit the JSON and click Save to apply changes'
                        : 'Import functionality coming soon'}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={handleCloseJsonModal}
                      className='px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors text-m-s'
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!!jsonModalError || isJsonModalLoading}
                      className={cn(
                        'px-4 py-2 rounded transition-colors text-m-s font-semibold flex items-center gap-2',
                        jsonModalError
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      )}
                    >
                      {isJsonModalLoading ? <span className='loading spin' /> : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
    </>
  )
}
